import Grid from '@material-ui/core/Grid';
import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Input,
    Button,
    Menu
} from '@material-ui/core';
import { DatePicker, StaticDatePicker } from '@material-ui/pickers';
import FormSection from '../../../components/atom/FormSection';
import moment from 'moment';
import { useSwaggerClient } from '../../../hooks/useSwaggerClient';

export default function BookingItemTimeslotSettings({
    values,
    setFieldValue,
    bookingCanModify,
    datepickerInline = false
}) {
    const changeMode = datepickerInline || values.startChangeMode || false;
    const setChangeMode = newValue => setFieldValue('startChangeMode', newValue);
    const [selectedTimeslot, setSelectedTimeslot] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const originalDate = values?.original_activity_start;
    const [currentMonth, setCurrentMonth] = useState(null);
    const [initialized, setInitialized] = useState(false);

    // Initialize selectedDate and currentMonth from values.activity_start
    useEffect(() => {
        if (!initialized && values.activity_start) {
            setSelectedDate(moment(values.activity_start));
            setCurrentMonth(moment(values.activity_start).startOf('month'));
            setInitialized(true);
        }
    }, [values.activity_start, initialized]);

    // Fetch timeslots for the entire month to determine available dates
    const [monthlyTimeslots] = useSwaggerClient(
        values.activity_id && currentMonth ? 'activities' : false,
        'getTimeslotsForActivity',
        {
            activityId: values.activity_id,
            fromDate: currentMonth?.format('YYYY-MM-DD'),
            toDate: currentMonth
                ?.clone()
                .endOf('month')
                .format('YYYY-MM-DD'),
            groupSize: 1
        },
        [currentMonth?.format('YYYY-MM'), values.activity_id],
        data => data.timeslots || []
    );

    // Get timeslots for the selected date
    const timeslotsForSelectedDate = useMemo(() => {
        if (!selectedDate) return [];
        const items =
            monthlyTimeslots?.filter(
                e => e.start_time && moment(e.start_time).isSame(selectedDate, 'day')
            ) || [];
        // unique by start_time
        const uniqueItems = Array.from(new Map(items.map(i => [i['start_time'], i])).values());
        const mappedItems = uniqueItems.map(t => ({
            ...t,
            timeslotValue: moment(t.start_time).format() // Use ISO string as value
        }));

        // If no timeslots found but we have a current activity_start for this date,
        // add it as a fake timeslot so the dropdown isn't empty on initial load
        if (mappedItems.length === 0 && values.activity_start) {
            const activityStartMoment = moment(values.activity_start);
            if (activityStartMoment.isSame(selectedDate, 'day')) {
                mappedItems.push({
                    start_time: values.activity_start,
                    end_time: values.activity_end,
                    timeslotValue: activityStartMoment.format(),
                    seats_available: 0,
                    seats_sold: 0,
                    limited_tickets: false
                });
            }
        }

        return mappedItems;
    }, [monthlyTimeslots, selectedDate, values.activity_start, values.activity_end]);

    // Initialize selected timeslot based on current activity_start
    useEffect(() => {
        if (values.activity_start) {
            const currentTimeslot = timeslotsForSelectedDate.find(t =>
                moment(t.start_time).isSame(moment(values.activity_start), 'minute')
            );
            if (currentTimeslot) {
                setSelectedTimeslot(currentTimeslot.timeslotValue);
            }
        }
    }, [values.activity_start, timeslotsForSelectedDate]);

    // Handle date selection from calendar
    const handleDateChange = newDate => {
        setSelectedDate(moment(newDate));

        // Check if we need to fetch data for a different month
        const newMonth = moment(newDate).startOf('month');
        if (!newMonth.isSame(currentMonth, 'month')) {
            setCurrentMonth(newMonth);
        }

        // Reset timeslot selection
        setSelectedTimeslot('');
    };

    return (
        <FormSection title="Start time">
            <Grid item xs={12}>
                <h5>Current start time:</h5>
                {!changeMode && (
                    <span>{moment(originalDate).format('ddd MMM DD, YYYY [at] h:mm A')}</span>
                )}
                {changeMode && (
                    <strike>{moment(originalDate).format('ddd MMM DD, YYYY [at] h:mm A')}</strike>
                )}
            </Grid>
            {!changeMode && (
                <Grid item xs={12}>
                    <Button
                        type="button"
                        variant="outlined"
                        fullWidth
                        onClick={() => setChangeMode(true)}
                        disabled={!bookingCanModify}
                    >
                        Change start time
                    </Button>
                </Grid>
            )}
            {changeMode && (
                <Grid item xs={12}>
                    <h5>New start time:</h5>
                </Grid>
            )}
            {changeMode && (
                <Grid item xs={12}>
                    {!datepickerInline && (
                        <DatePicker
                            label="Select date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            disabled={!bookingCanModify}
                            inputFormat="MM/DD/YYYY"
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    helperText=""
                                />
                            )}
                            onMonthChange={newMonth => {
                                if (newMonth) {
                                    const monthStart = moment(newMonth).startOf('month');
                                    setCurrentMonth(monthStart);
                                }
                            }}
                            shouldDisableDate={date => {
                                // Disable dates that do not have any available timeslots
                                const hasTimeslots = monthlyTimeslots?.some(t =>
                                    moment(t.start_time).isSame(date, 'day')
                                );
                                return !hasTimeslots;
                            }}
                        />
                    )}
                    {datepickerInline && (
                        <StaticDatePicker
                            label="Select date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            disabled={!bookingCanModify}
                            inputFormat="MM/DD/YYYY"
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    helperText=""
                                />
                            )}
                            onMonthChange={newMonth => {
                                if (newMonth) {
                                    const monthStart = moment(newMonth).startOf('month');
                                    setCurrentMonth(monthStart);
                                }
                            }}
                            shouldDisableDate={date => {
                                // Disable dates that do not have any available timeslots
                                const hasTimeslots = monthlyTimeslots?.some(t =>
                                    moment(t.start_time).isSame(date, 'day')
                                );
                                return !hasTimeslots;
                            }}
                        />
                    )}
                </Grid>
            )}

            {changeMode && (
                <Grid item xs={12}>
                    {timeslotsForSelectedDate.length > 0 ? (
                        <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            disabled={!bookingCanModify}
                        >
                            <InputLabel>Available start times</InputLabel>
                            <Select
                                value={selectedTimeslot || ''}
                                onChange={ev => {
                                    const newTimeslot = ev.target.value;
                                    const selected = timeslotsForSelectedDate.find(
                                        t => t.timeslotValue === newTimeslot
                                    );
                                    if (selected) {
                                        setSelectedTimeslot(newTimeslot);
                                        setFieldValue('activity_start', selected.start_time);
                                        setFieldValue('activity_end', selected.end_time);
                                    }
                                }}
                                input={<Input />}
                                label="Available start times"
                            >
                                {timeslotsForSelectedDate.map(e => {
                                    let seats;
                                    // if overbooked, show the overbooked amount in the title
                                    if (e.seats_sold > e.seats_available) {
                                        seats = ` (overbooked by ${e.seats_sold -
                                            e.seats_available})`;
                                    } else if (e.seats_sold === e.seats_available) {
                                        seats = ' (sold out)';
                                    } else {
                                        seats = ` (${e.seats_available - e.seats_sold} left)`;
                                    }
                                    return (
                                        <MenuItem key={e.timeslotValue} value={e.timeslotValue}>
                                            {moment(e.start_time).format('MM/DD/YYYY [at] h:mm A')}{' '}
                                            {e.limited_tickets && seats}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    ) : (
                        <small>No available start times on this date.</small>
                    )}
                </Grid>
            )}
            {changeMode && !datepickerInline && (
                <Grid item xs={12}>
                    <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => {
                            setChangeMode(false);
                            setSelectedDate(moment(originalDate));
                            const lengthOfActivity = moment(values.activity_end).diff(
                                moment(values.activity_start),
                                'minutes'
                            );
                            setFieldValue(
                                'activity_end',
                                moment(originalDate)
                                    .add(lengthOfActivity, 'minutes')
                                    .toISOString()
                            );
                            setFieldValue('activity_start', moment(originalDate).toISOString());
                            setSelectedTimeslot('');
                        }}
                        disabled={!bookingCanModify}
                    >
                        Keep original start time
                    </Button>
                </Grid>
            )}
        </FormSection>
    );
}
